import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Configuration options for the infinite scroll hook
 */
interface UseInfiniteScrollOptionsI {
  hasNextPage: boolean
  isLoading: boolean
  onLoadMore: () => void | Promise<void>
  /** Intersection threshold (0-1) that determines when the callback is triggered */
  threshold?: number
  /** Root margin for the intersection observer */
  rootMargin?: string
}

/**
 * Return value of the infinite scroll hook
 */
interface UseInfiniteScrollReturnI {
  loadMoreRef: (node: HTMLDivElement | null) => void
  isIntersecting: boolean
}

/**
 * Custom hook that implements infinite scrolling functionality using Intersection Observer API
 *
 * @param options - Configuration object for the infinite scroll behavior
 * @returns Object containing the ref to attach to trigger element and intersection state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, hasNextPage, isLoading, fetchNextPage } = useInfiniteQuery(...)
 *
 *   const { loadMoreRef, isIntersecting } = useInfiniteScroll({
 *     hasNextPage,
 *     isLoading,
 *     onLoadMore: fetchNextPage,
 *     threshold: 0.5,
 *     rootMargin: '100px'
 *   })
 *
 *   return (
 *     <div>
 *       {data.map(item => <div key={item.id}>{item.name}</div>)}
 *       <div ref={loadMoreRef}>
 *         {isLoading && <LoadingSpinner />}
 *       </div>
 *     </div>
 *   )
 * }
 * ```
 */
export function useInfiniteScroll({
  hasNextPage,
  isLoading,
  onLoadMore,
  threshold = 0,
  rootMargin = '200px',
}: UseInfiniteScrollOptionsI): UseInfiniteScrollReturnI {
  const [isIntersecting, setIsIntersecting] = useState(false)
  const loadingGateRef = useRef(false) // Gate to prevent multiple simultaneous loading operations
  const observerRef = useRef<IntersectionObserver | null>(null)

  /**
   * Handles intersection changes from the IntersectionObserver
   * Triggers loading when conditions are met and prevents duplicate requests
   */
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries
      setIsIntersecting(entry.isIntersecting)

      if (
        !entry.isIntersecting ||
        !hasNextPage ||
        isLoading ||
        loadingGateRef.current
      )
        return

      loadingGateRef.current = true // Close the loading gate to prevent duplicate requests

      Promise.resolve(onLoadMore())
        .catch(() => {
          // Silently handle errors - they should be handled by the calling component
        })
        .finally(() => {
          loadingGateRef.current = false
        })
    },
    [hasNextPage, isLoading, onLoadMore]
  )

  /**
   * Callback ref that sets up the IntersectionObserver when attached to an element
   * This pattern allows for dynamic ref assignment and automatic cleanup
   */
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }

      if (!node || typeof IntersectionObserver === 'undefined') return

      const observer = new IntersectionObserver(handleIntersection, {
        threshold,
        rootMargin,
      })
      observerRef.current = observer
      observer.observe(node)
    },
    [handleIntersection, threshold, rootMargin]
  )

  // Global cleanup effect that runs when the component unmounts
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [])

  return { loadMoreRef, isIntersecting }
}
